import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NotificationViewport } from './NotificationViewport';
import type {
  ErrorNotificationOptions,
  ErrorNotificationState,
  NotificationDispatcher,
  NotificationId,
  NotificationState,
  SuccessNotificationOptions,
  SuccessNotificationState,
  UploadNotificationOptions,
  UploadNotificationState,
  WarningNotificationOptions,
  WarningNotificationState,
} from './notification-types';
import {
  createNotificationId,
  normalizeProgress,
  notificationCloseAnimationMs,
} from './notification-utils';
import './NotificationProvider.css';

export type {
  ErrorNotificationOptions,
  NotificationDispatcher,
  NotificationId,
  NotificationType,
  SuccessNotificationOptions,
  UploadNotificationOptions,
  WarningNotificationOptions,
} from './notification-types';

type NotificationDraft =
  | Omit<UploadNotificationState, 'id' | 'isClosing'>
  | Omit<SuccessNotificationState, 'id' | 'isClosing'>
  | Omit<ErrorNotificationState, 'id' | 'isClosing'>
  | Omit<WarningNotificationState, 'id' | 'isClosing'>;

const NotificationContext = createContext<NotificationDispatcher | null>(null);

export const NotificationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationState[]>([]);
  const closeTimeoutsRef = useRef(new Map<NotificationId, number>());

  const addNotification = useCallback(
    (notification: NotificationDraft): NotificationId => {
      const id = createNotificationId();
      setNotifications((current) => [
        ...current,
        {
          id,
          ...notification,
        } as NotificationState,
      ]);
      return id;
    },
    [],
  );

  const close = useCallback((id: NotificationId) => {
    if (closeTimeoutsRef.current.has(id)) return;

    setNotifications((current) =>
      current.map((notification) =>
        notification.id === id
          ? {
              ...notification,
              isClosing: true,
            }
          : notification,
      ),
    );

    const timeoutId = window.setTimeout(() => {
      closeTimeoutsRef.current.delete(id);
      setNotifications((current) =>
        current.filter((notification) => notification.id !== id),
      );
    }, notificationCloseAnimationMs);

    closeTimeoutsRef.current.set(id, timeoutId);
  }, []);

  useEffect(() => {
    return () => {
      closeTimeoutsRef.current.forEach((timeoutId) =>
        window.clearTimeout(timeoutId),
      );
      closeTimeoutsRef.current.clear();
    };
  }, []);

  const showUpload = useCallback(
    ({ title, progress = 0 }: UploadNotificationOptions): NotificationId =>
      addNotification({
        type: 'upload',
        title,
        progress: normalizeProgress(progress),
      }),
    [addNotification],
  );

  const updateUpload = useCallback(
    (id: NotificationId, options: Partial<UploadNotificationOptions>) => {
      setNotifications((current) =>
        current.map((notification) => {
          if (notification.id !== id || notification.type !== 'upload') {
            return notification;
          }

          const progress =
            options.progress === undefined
              ? notification.progress
              : normalizeProgress(options.progress);

          return {
            ...notification,
            ...options,
            progress,
          };
        }),
      );

      if (
        options.progress !== undefined &&
        normalizeProgress(options.progress) >= 100
      ) {
        close(id);
      }
    },
    [close],
  );

  const showSuccess = useCallback(
    ({
      title,
      description,
      autoClose = true,
    }: SuccessNotificationOptions): NotificationId =>
      addNotification({
        type: 'success',
        title,
        description,
        autoClose,
      }),
    [addNotification],
  );

  const showError = useCallback(
    ({
      title,
      description,
      autoClose = true,
    }: ErrorNotificationOptions): NotificationId =>
      addNotification({
        type: 'error',
        title,
        description,
        autoClose,
      }),
    [addNotification],
  );

  const showWarning = useCallback(
    ({
      title,
      description,
      autoClose = true,
    }: WarningNotificationOptions): NotificationId =>
      addNotification({
        type: 'warning',
        title,
        description,
        autoClose,
      }),
    [addNotification],
  );

  const clear = useCallback(() => {
    closeTimeoutsRef.current.forEach((timeoutId) =>
      window.clearTimeout(timeoutId),
    );
    closeTimeoutsRef.current.clear();
    setNotifications([]);
  }, []);

  const dispatcher = useMemo<NotificationDispatcher>(
    () => ({
      showUpload,
      updateUpload,
      showSuccess,
      showError,
      showWarning,
      close,
      clear,
    }),
    [
      clear,
      close,
      showError,
      showUpload,
      showSuccess,
      showWarning,
      updateUpload,
    ],
  );

  return (
    <NotificationContext.Provider value={dispatcher}>
      {children}
      <NotificationViewport notifications={notifications} onClose={close} />
    </NotificationContext.Provider>
  );
};

export function useNotifications(): NotificationDispatcher {
  const dispatcher = useContext(NotificationContext);
  if (!dispatcher) {
    throw new Error('useNotifications must be used inside NotificationProvider.');
  }

  return dispatcher;
}
